import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { formatDate, formatEuro, clienteNome, statoLabel, Modal, ConfirmDialog, LoadingSpinner, Pagination } from '../components/shared';


const MODELLI = ['730', 'Redditi', 'Redditi Semplificato'];

// ─── INPUT ANNO LIBERO ────────────────────────────────────────────────────────
function AnnoInput({ value, onChange, placeholder = 'Anno…', style = {} }) {
  return (
    <input
      type="number"
      min="1990" max="2099"
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ width: 90, textAlign: 'center', fontFamily: 'monospace', ...style }}
      className="form-control"
    />
  );
}

function RedditiForm({ initial = {}, clientiList, onSave, onClose, loading }) {
  const [d, setD] = useState({
    id_cliente: '', tipo: '730', descrizione: '', data_scad: '',
    importo: '', stato: 'APERTA', note: '', ...initial,
    id_cliente: initial.id_cliente || '',
    importo: initial.importo || '',
  });
  const [anno, setAnno]     = useState(new Date().getFullYear() - 1);
  const [modello, setModello] = useState('730');
  const set = (k, v) => setD(p => ({ ...p, [k]: v }));

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave({ ...d, tipo: '730', descrizione: d.descrizione || `${modello} ${anno + 1} - redditi ${anno}` }); }}>
      <div className="form-grid">
        <div className="form-group form-full">
          <label className="form-label">Cliente *</label>
          <select className="form-control" value={d.id_cliente} onChange={e => set('id_cliente', e.target.value)} required>
            <option value="">— Seleziona cliente —</option>
            {clientiList.filter(c => c.tipo === 'PF').map(c => <option key={c.id} value={c.id}>{clienteNome(c)} — {c.cf}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Modello</label>
          <select className="form-control" value={modello} onChange={e => setModello(e.target.value)}>
            {MODELLI.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Anno d'imposta</label>
          <AnnoInput value={anno} onChange={setAnno} placeholder="Es. 2024" />
        </div>
        <div className="form-group">
          <label className="form-label">Data Scadenza *</label>
          <input className="form-control" type="date" value={d.data_scad} onChange={e => set('data_scad', e.target.value)} required />
        </div>
        <div className="form-group">
          <label className="form-label">Importo a debito (€)</label>
          <input className="form-control" type="number" step="0.01" value={d.importo} onChange={e => set('importo', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Stato</label>
          <select className="form-control" value={d.stato} onChange={e => set('stato', e.target.value)}>
            <option value="APERTA">Aperta</option>
            <option value="PREPARATA">Preparata</option>
            <option value="INVIATA">Inviata</option>
            <option value="SOSPESA">Sospesa</option>
            <option value="NON_PREVISTA">Non prevista</option>
            <option value="ALTRO">Altro</option>
          </select>
        </div>
        <div className="form-group form-full">
          <label className="form-label">Descrizione</label>
          <input className="form-control" value={d.descrizione} onChange={e => set('descrizione', e.target.value)} placeholder={`${modello} ${Number(anno) + 1} - redditi ${anno}`} />
        </div>
        <div className="form-group form-full">
          <label className="form-label">Note</label>
          <textarea className="form-control" value={d.note} onChange={e => set('note', e.target.value)} rows={3} />
        </div>
      </div>
      <div className="modal-footer" style={{ padding: '16px 0 0', borderTop: '1px solid var(--border)', marginTop: 20 }}>
        <button type="button" className="btn" onClick={onClose}>Annulla</button>
        <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Salvataggio…' : 'Salva'}</button>
      </div>
    </form>
  );
}


// ─── SORTABLE TH ─────────────────────────────────────────────────────────────
function SortTh({ label, field, sort, dir, onSort }) {
  const active = sort === field;
  return (
    <th onClick={() => onSort(field)} style={{
      cursor:'pointer', userSelect:'none', whiteSpace:'nowrap',
      color: active ? 'var(--accent)' : undefined,
      background: active ? 'var(--accent-light)' : undefined,
    }}>
      {label}{active ? (dir==='asc' ? ' ↑' : ' ↓') : ''}
    </th>
  );
}

export default function RedditiPF({ addToast }) {
  const [data, setData]       = useState({ rows: [], total: 0 });
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ]             = useState('');
  const [statoF, setStatoF]   = useState('');
  const [page, setPage]       = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [delItem, setDelItem]   = useState(null);
  const [saving, setSaving]     = useState(false);

  useEffect(() => { api.getClienti({ limit: 200 }).then(d => setClienti(d.rows)); }, []);

  const [sort, setSort] = useState('data_scad');
  const [dir, setDir]   = useState('asc');

  const handleSort = (field) => {
    if (sort === field) setDir(d => d==='asc'?'desc':'asc');
    else { setSort(field); setDir('asc'); }
    setPage && setPage(1);
  };

  const sortRows = (rows) => [...rows].sort((a,b) => {
    let va = a[sort], vb = b[sort];
    if (va==null) va=''; if (vb==null) vb='';
    const cmp = String(va).localeCompare(String(vb),'it',{numeric:true});
    return dir==='asc' ? cmp : -cmp;
  });

  const sortProps = { sort, dir, onSort: handleSort };

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, limit: 20, tipo: '730' };
    if (q) params.q = q;
    if (statoF) params.stato = statoF;
    api.getScadenze(params).then(setData).finally(() => setLoading(false));
  }, [q, statoF, page]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (editItem) { await api.updateScadenza(editItem.id, formData); addToast('Aggiornato', 'success'); setEditItem(null); }
      else { await api.createScadenza(formData); addToast('Creato', 'success'); setShowForm(false); }
      load();
    } catch (e) { addToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await api.deleteScadenza(delItem.id); addToast('Eliminato', 'success'); setDelItem(null); load(); }
    catch (e) { addToast(e.message, 'error'); }
  };

  const stats = {
    aperti: data.rows.filter(r => r.stato === 'APERTA').length,
    inCorso: data.rows.filter(r => r.stato === 'IN_CORSO').length,
    presentati: data.rows.filter(r => r.stato === 'COMPLETATA').length,
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <div className="page-title">Redditi Persone Fisiche</div>
          <div className="page-subtitle">Mod. 730 e dichiarazioni redditi PF</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ Nuovo 730</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 24 }}>
        {[['Da lavorare', stats.aperti, 'var(--c-red)'], ['In lavorazione', stats.inCorso, 'var(--c-amber)'], ['Presentati', stats.presentati, 'var(--c-green)']].map(([l, v, c]) => (
          <div key={l} className="kpi-card">
            <div className="kpi-accent" style={{ background: c }} />
            <div className="kpi-label">{l}</div>
            <div className="kpi-value">{v}</div>
          </div>
        ))}
      </div>

      <div className="filters-bar">
        <div className="search-box">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5"/><path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          <input placeholder="Cerca cliente…" value={q} onChange={e => { setQ(e.target.value); setPage(1); }} />
        </div>
        <select className="form-control" style={{ width: 'auto' }} value={statoF} onChange={e => { setStatoF(e.target.value); setPage(1); }}>
          <option value="">Tutti gli stati</option>
          <option value="APERTA">Aperta</option>
          <option value="IN_CORSO">In lavorazione</option>
          <option value="COMPLETATA">Presentata</option>
          <option value="ANNULLATA">Annullata</option>
        </select>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <LoadingSpinner /> : (
            <table>
              <thead><tr>
                <SortTh label="Cliente" field="denominazione" {...sortProps} />
                <SortTh label="Descrizione" field="descrizione" {...sortProps} />
                <SortTh label="Scadenza" field="data_scad" {...sortProps} />
                <SortTh label="Importo" field="importo" {...sortProps} />
                <SortTh label="Stato" field="stato" {...sortProps} />
                <th>Azioni</th>
              </tr></thead>
              <tbody>
                {sortRows(data.rows).map(s => (
                  <tr key={s.id}>
                    <td className="fw-500">{clienteNome(s) || '—'}</td>
                    <td className="td-mono">{s.cf || '—'}</td>
                    <td>{s.descrizione || '—'}</td>
                    <td style={{ fontSize: 12 }}>{formatDate(s.data_scad)}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{s.importo ? formatEuro(s.importo) : '—'}</td>
                    <td>
                      <span className={`pill ${s.stato === 'COMPLETATA' ? 'pill-green' : s.stato === 'IN_CORSO' ? 'pill-amber' : s.stato === 'ANNULLATA' ? 'pill-gray' : 'pill-red'}`}>
                        {s.stato === 'COMPLETATA' ? 'Presentata' : statoLabel(s.stato)}
                      </span>
                    </td>
                    <td><div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-sm" onClick={() => setEditItem(s)}>Modifica</button>
                      <button className="btn btn-sm btn-danger" onClick={() => setDelItem(s)}>×</button>
                    </div></td>
                  </tr>
                ))}
                {data.rows.length === 0 && <tr><td colSpan={7}><div className="empty-state"><div className="empty-state-icon">📊</div><div className="empty-state-title">Nessuna pratica redditi</div></div></td></tr>}
              </tbody>
            </table>
          )}
        </div>
        <Pagination page={page} total={data.total} limit={20} onPage={setPage} />
      </div>

      {(showForm || editItem) && (
        <Modal title={editItem ? 'Modifica Pratica' : 'Nuovo 730 / Redditi'} onClose={() => { setShowForm(false); setEditItem(null); }}>
          <RedditiForm initial={editItem || {}} clientiList={clienti} onSave={handleSave} onClose={() => { setShowForm(false); setEditItem(null); }} loading={saving} />
        </Modal>
      )}
      {delItem && <ConfirmDialog message={`Eliminare la pratica "${delItem.descrizione}"?`} onConfirm={handleDelete} onCancel={() => setDelItem(null)} />}
    </div>
  );
}
